import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { ShieldCheck, Hourglass, CheckCircle, XCircle, Upload, Loader2, IdCard } from 'lucide-react';

const DOC_TYPES = [
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'passport', label: 'Passport' },
];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function Kyc() {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ docType: 'aadhaar', docNumber: '' });
  const [images, setImages] = useState({ docFrontImage: '', docBackImage: '', selfieImage: '' });
  const [previews, setPreviews] = useState({ docFrontImage: '', docBackImage: '', selfieImage: '' });

  const fetchKyc = () => {
    setLoading(true);
    api.get('/kyc/my').then(r => setKyc(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchKyc(); }, []);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFile = async (field, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Image must be under 4MB');
      return;
    }
    const base64 = await fileToBase64(file);
    setImages(prev => ({ ...prev, [field]: base64 }));
    setPreviews(prev => ({ ...prev, [field]: base64 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.docNumber.trim()) return toast.error('Enter document number');
    if (!images.docFrontImage) return toast.error('Front image of document is required');
    setSubmitting(true);
    try {
      const res = await api.post('/kyc/submit', { ...form, ...images });
      toast.success(res.data.message);
      setKyc(res.data.kyc);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit KYC');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { color: '#f59e0b', icon: Hourglass, label: 'Pending Review' },
      approved: { color: '#22c55e', icon: CheckCircle, label: 'Approved' },
      rejected: { color: '#ef4444', icon: XCircle, label: 'Rejected' },
    };
    const s = map[status] || { color: 'var(--text-muted)', icon: ShieldCheck, label: 'Not Submitted' };
    const Icon = s.icon;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: s.color, fontWeight: 700 }}>
        <Icon size={16} /> {s.label}
      </span>
    );
  };

  const canSubmit = !kyc || kyc.status === 'rejected';

  return (
    <DashboardLayout>
      <h1 className="page-title">KYC Verification</h1>
      <p className="page-subtitle">Verify your identity to unlock withdrawals and higher package limits</p>

      {loading ? (
        <div className="loader-wrap"><div className="loader"></div></div>
      ) : (
        <>
          {kyc && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><IdCard size={18} /> Your KYC Status</h3>
                {statusBadge(kyc.status)}
              </div>
              <div style={{ color: 'var(--text-light)', fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div>Document: <strong style={{ color: 'var(--primary)' }}>{DOC_TYPES.find(d => d.value === kyc.docType)?.label}</strong></div>
                <div>Number: <strong>{kyc.docNumber}</strong></div>
                <div>Submitted: {new Date(kyc.createdAt).toLocaleDateString('en-IN')}</div>
                {kyc.status === 'rejected' && (
                  <div className="alert alert-warning" style={{ marginTop: 10 }}>
                    Reason: {kyc.adminNote || 'Please re-check and re-submit your documents.'}
                  </div>
                )}
                {kyc.status === 'approved' && (
                  <div className="alert alert-info" style={{ marginTop: 10 }}>
                    Verified on {new Date(kyc.reviewedAt).toLocaleDateString('en-IN')}. No further action needed.
                  </div>
                )}
              </div>
            </div>
          )}

          {canSubmit && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ShieldCheck size={18} /> {kyc ? 'Re-submit KYC' : 'Submit KYC'}
                </h3>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Document Type *</label>
                  <select name="docType" className="form-control" value={form.docType} onChange={handleChange}>
                    {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Document Number *</label>
                  <input
                    type="text"
                    name="docNumber"
                    className="form-control"
                    placeholder="Enter document number"
                    value={form.docNumber}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Document Front Image *</label>
                  <input type="file" accept="image/*" className="form-control"
                    onChange={e => handleFile('docFrontImage', e.target.files[0])} />
                  {previews.docFrontImage && <img src={previews.docFrontImage} alt="front preview" style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, border: '1px solid rgba(var(--primary-rgb),0.3)' }} />}
                </div>

                <div className="form-group">
                  <label>Document Back Image (optional)</label>
                  <input type="file" accept="image/*" className="form-control"
                    onChange={e => handleFile('docBackImage', e.target.files[0])} />
                  {previews.docBackImage && <img src={previews.docBackImage} alt="back preview" style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, border: '1px solid rgba(var(--primary-rgb),0.3)' }} />}
                </div>

                <div className="form-group">
                  <label>Selfie (optional, recommended)</label>
                  <input type="file" accept="image/*" className="form-control"
                    onChange={e => handleFile('selfieImage', e.target.files[0])} />
                  {previews.selfieImage && <img src={previews.selfieImage} alt="selfie preview" style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, border: '1px solid rgba(var(--primary-rgb),0.3)' }} />}
                </div>

                <div className="alert alert-info" style={{ fontSize: '0.82rem', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <ShieldCheck size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>Make sure all details are clearly visible. Admin will verify within 24-48 hours.</span>
                </div>

                <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                  {submitting ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Loader2 size={16} className="spin" /> Submitting...</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Upload size={16} /> Submit KYC</span>
                  )}
                </button>
              </form>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}